import { useLocation } from 'react-router-dom';
import { useQueryParams } from '@/hooks/useQueryParams';
import type { AuthLocationState } from '../types';

type From = {
  destination: string;
  isServerRoute: boolean;
};

const SERVER_NEW_PATH = '/new';

// Paths the server renders, so returning to one after sign-in needs a real
// navigation rather than a router push. /oauth/authorize is the authorization
// server resuming a connector's grant: it reads the session cookie and answers
// with the consent screen, neither of which a client-side route can do.
const SERVER_PATHS = [SERVER_NEW_PATH, '/billing', '/oauth/authorize'];

export const isServerPath = (destination: string): boolean =>
  SERVER_PATHS.some((path) => {
    if (!destination.startsWith(path)) return false;
    const next = destination.charAt(path.length);
    return next === '' || next === '/' || next === '?';
  });

const locationToHref = (location?: Location): string | undefined => {
  if (!location) {
    return undefined;
  }
  return `${location.pathname}${location.search}`;
};

export const useFrom = (): From | undefined => {
  const { queryParams } = useQueryParams();
  const { state: locationState } = useLocation() as AuthLocationState;
  const from = queryParams.get('from') || locationToHref(locationState?.from);
  const isNew = Boolean(queryParams.get('new') || from === SERVER_NEW_PATH);

  let destination = isNew ? SERVER_NEW_PATH : from;

  // If we're redirecting to /new and have a templateId, preserve it
  const templateId = queryParams.get('templateId');
  if (isNew && templateId) {
    destination = `${SERVER_NEW_PATH}?templateId=${encodeURIComponent(templateId)}`;
  }

  if (!destination) {
    return undefined;
  }

  return {
    destination,
    isServerRoute: isServerPath(destination),
  };
};
